import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Country, State, City } from 'country-state-city';
import axios from 'axios';
import useCustomModal from '../hooks/useCustomModal';
import PaymentConfirmationModal from '../components/PaymentConfirmationModal';

// Helpers for Date Formatting (DD-MM-YYYY)
const formatDateStr = (dateString) => {
  if (!dateString) return '';
  const parts = dateString.split('-');
  if (parts.length === 3) return `${parts[2]}-${parts[1]}-${parts[0]}`;
  return dateString;
};

const formatDateObj = (dateObj) => {
  if (!dateObj) return '';
  const d = new Date(dateObj);
  if (isNaN(d.getTime())) return dateObj;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const year = d.getFullYear();

  let hours = d.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const minutes = String(d.getMinutes()).padStart(2, '0');

  return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
};

const BookingModal = ({ show, onClose, selection, onSuccess, dateStr, timeStr, duration, showModal, isTestingOtpEnabled }) => {
  const [phone, setPhone] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  // Auto-fill logic for testing
  useEffect(() => {
    if (show && isTestingOtpEnabled && !phone) {
      setPhone('9845098450');
    }
  }, [show, isTestingOtpEnabled, phone]);

  const [error, setError] = useState('');

  if (!show || !selection || !selection.category) return null;

  // Calc Checkout
  const checkIn = new Date(`${dateStr} ${timeStr}`);
  const startD = new Date(dateStr);
  const endD = new Date(startD);
  endD.setDate(endD.getDate() + parseInt(duration));
  const endStr = endD.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  const startStr = startD.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });

  const handleBookClick = async () => {
    if (!phone || phone.length !== 10) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.get(`/api/public/user/phone/${phone}`);
      if (response.data.status === 'success') {
        if (response.data.data && response.data.data.email_id) {
          setUserEmail(response.data.data.email_id);
        } else {
          setUserEmail('');
        }
        // Send OTP
        try {
          const res = await axios.post('/api/public/auth/send-otp', { phone });
          // If testing enabled, auto-fill OTP from response
          if (isTestingOtpEnabled && res.data.otp) {
            setOtp(res.data.otp);
          }
          showModal(`OTP Sent successfully`, 'success');
        } catch (e) {
          console.error(e);
        }
        setShowOtp(true);
      }
    } catch (err) {
      // User Not Found or Error
      if (err.response && err.response.status === 404) {
        // Not Found -> Pass phone to next step for registration
        // No token needed yet, will be created on registration
        onSuccess(null, phone);
      } else {
        console.error(err);
        showModal("Error searching user", 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setVerifyingOtp(true);
    try {
      const res = await axios.post('/api/public/auth/verify-otp', { phone, otp });
      if (res.data.status === 'success' && res.data.verified) {
        // Success
        if (res.data.token) {
          localStorage.setItem("uid", res.data.token);
        }
        const user = res.data.user; // Assuming structure based on GuestDetailsForm
        onSuccess(user, phone);
      } else {
        showModal("Invalid OTP", 'error');
      }
    } catch (e) {
      console.error(e);
      const errorMsg = e.response?.data?.message || "OTP Verification Failed";
      showModal(errorMsg, 'error');
    } finally {
      setVerifyingOtp(false);
    }
  };

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body pt-0 pb-4 px-4">
            <div className="row">
              {/* Left: Details */}
              <div className="col-md-6 border-end">
                <h4 className="fw-bold mb-1" style={{ color: '#333' }}>
                  {selection.building.building_name}
                </h4>
                <h3 className="fw-bold text-uppercase" style={{ color: '#000' }}>
                  {selection.category.category_name}
                </h3>

                <div className="mt-4">
                  <small className="text-muted fw-bold">Check-In:</small>
                  <div className="fs-5">{startStr} - {timeStr}</div>
                </div>

                <div className="mt-3">
                  <small className="text-muted fw-bold">Check-Out:</small>
                  <div className="fs-5">{endStr} - {timeStr}</div>
                </div>
              </div>

              {/* Right: Input */}
              <div className="col-md-6 d-flex flex-column justify-content-center px-4">
                <label className="fw-bold mb-2">Mobile No.</label>
                <input
                  type="text"
                  className="form-control form-control-lg mb-3 rounded-pill"
                  placeholder="Mobile No."
                  maxLength="10"
                  value={phone}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val)) {
                      setPhone(val);
                      setError(''); // Clear error on type
                    }
                  }}
                  disabled={showOtp}
                />
                {error && <div className="text-danger small mb-2" style={{ marginTop: '-10px' }}>{error}</div>}

                {showOtp && (
                  <div className="mb-3">
                    <label className="fw-bold mb-2">Enter OTP</label>
                    <input
                      type="text"
                      className="form-control form-control-lg rounded-pill mb-2"
                      placeholder="Enter the 6-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      maxLength="6"
                    />
                    <div className="text-center text-muted mt-2 px-2 mb-2" style={{ fontSize: '0.75rem', lineHeight: '1.4' }}>
                      OTP sent via WhatsApp to {phone}{userEmail ? `, Email to ${userEmail}` : ''}.
                    </div>
                  </div>
                )}

                {!showOtp ? (
                  <button
                    className="btn btn-dark btn-lg w-100 rounded-pill"
                    onClick={handleBookClick}
                    disabled={loading}
                  >
                    {loading ? 'Processing...' : 'Next'}
                  </button>
                ) : (
                  <button
                    className="btn btn-success btn-lg w-100 rounded-pill"
                    onClick={handleVerifyOtp}
                    disabled={verifyingOtp}
                  >
                    {verifyingOtp ? 'Verifying...' : 'Verify & Proceed'}
                  </button>
                )}

                {!showOtp && (
                  <div className="text-center mt-3 text-muted" style={{ fontSize: '0.8rem' }}>
                    OTP is tracked via WhatsApp and Email. Use only the latest OTP. Wait 60 seconds.
                  </div>
                )}

                {loading && <div className="text-center mt-2 text-muted small">Loading....</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const GuestDetailsForm = ({ selection, dateStr, timeStr, duration, onSuccess, initialUser, initialPhone, showModal, isTestingOtpEnabled }) => {
  // If initialUser is provided -> User is "found", pre-fill and skip "Search" UI
  // If initialPhone is provided but no User -> User "not found", pre-fill phone, show Reg Form

  // Derived State
  const [phone, setPhone] = useState(initialPhone || '');
  const [userFound, setUserFound] = useState(initialUser ? 'found' : (initialPhone ? 'not_found' : false));
  const [loadingUser, setLoadingUser] = useState(false);

  // Validation State
  const [errors, setErrors] = useState({});

  // OTP State
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  // Form Data
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    full_name: '',
    email_id: '',
    phone_num: initialPhone || '',
    address: '',
    town: '',
    state: '',
    country: '',
    postal_code: '',
    id_proof_type: 'Aadhar Card',
    id_proof_number: '',
  });

  // Auto-fill logic for testing
  useEffect(() => {
    if (isTestingOtpEnabled && !phone) {
      setPhone('9845098450');
    }
  }, [isTestingOtpEnabled, phone]);

  // Country State City State
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");

  // Sync selectedCountry/State ISO codes when formData.country/state changes (e.g. from existing User)
  useEffect(() => {
    if (formData.country) {
      const countryObj = Country.getAllCountries().find(c => c.name === formData.country);
      if (countryObj) {
        setSelectedCountry(countryObj.isoCode);
        if (formData.state) {
          const stateObj = State.getStatesOfCountry(countryObj.isoCode).find(s => s.name === formData.state);
          if (stateObj) setSelectedState(stateObj.isoCode);
        }
      }
    }
  }, [formData.country, formData.state]);

  const handleCountryChange = (e) => {
    const isoCode = e.target.value;
    const countryData = Country.getCountryByCode(isoCode);
    setSelectedCountry(isoCode);
    setSelectedState(""); // Reset state
    setFormData(prev => ({
      ...prev,
      country: countryData ? countryData.name : "",
      state: "",
      town: "",
      id_proof_type: isoCode === 'IN' ? 'Aadhar Card' : 'Passport' // Reset ID Type based on country
    }));
  };

  const handleStateChange = (e) => {
    const isoCode = e.target.value;
    const stateData = State.getStateByCodeAndCountry(isoCode, selectedCountry);
    setSelectedState(isoCode);
    setFormData(prev => ({
      ...prev,
      state: stateData ? stateData.name : "",
      town: ""
    }));
  };

  const handleCityChange = (e) => {
    setFormData(prev => ({ ...prev, town: e.target.value }));
  };

  // Populate if existing user found via Modal
  useEffect(() => {
    if (initialUser) {
      setFormData({
        _id: initialUser._id,
        first_name: initialUser.first_name || '',
        last_name: initialUser.last_name || '',
        full_name: initialUser.full_name || '',
        email_id: initialUser.email_id || '',
        phone_num: initialUser.phone_num || initialPhone,
        address: initialUser.address || '',
        town: initialUser.town || '',
        state: initialUser.state || '',
        country: initialUser.country || 'India',
        postal_code: initialUser.postal_code || '',
        id_proof_type: initialUser.id_proof_type || 'Aadhar Card',
        id_proof_number: initialUser.id_proof_number || ''
      });
      setUserFound('found');
    } else if (initialPhone) {
      // New user, just set phone
      setFormData(prev => ({ ...prev, phone_num: initialPhone }));
    }
  }, [initialUser, initialPhone]);


  const handlePhoneSearch = async () => {
    if (phone.length !== 10) {
      showModal('Please enter a valid 10-digit mobile number.', 'info');
      return;
    }
    setLoadingUser(true);
    try {
      const response = await axios.get(`/api/public/user/phone/${phone}`);
      setFormData({
        ...formData,
        ...response.data.data
      });
      // If found, maybe skip OTP? 
      // Existing logic: We populate and allow edit.
      // We will assume "User Found" = Verified for now or re-verify on Submit.
      // Actually, original code likely had logic to populate.
      if (response.data.status === 'success') {
        setUserFound('found');
        setFormData(prev => ({ ...prev, _id: response.data.data._id }));
        // No OTP needed if just searching? 
        // We'll keep it simple as per original behavior view
      }
    } catch (err) {
      if (err.response && (err.response.status === 404 || err.response.status === 400)) {
        setUserFound('not_found');
        setFormData({ ...formData, phone_num: phone });
        // Send OTP for new user?
        sendOtp();
      } else {
        showModal('Error searching user', 'error');
      }
    } finally {
      setLoadingUser(false);
    }
  };

  // ... (Keep handleVerifyOtp, handleChange, handleSubmit logic) ...
  // Note: handleSubmit already does auto-login (store token).

  // Send OTP Function
  const sendOtp = async () => {
    if (!formData.phone_num || formData.phone_num.length !== 10) return showModal("Invalid Phone", 'error');
    setLoadingUser(true);
    try {
      const res = await axios.post('/api/public/auth/send-otp', { phone: formData.phone_num });
      // If testing enabled, auto-fill OTP from response
      if (isTestingOtpEnabled && res.data.otp) {
        setOtp(res.data.otp);
      }
      showModal(`OTP Sent successfully`, 'success');
      setOtpSent(true);
      setShowOtpInput(true);
    } catch (e) {
      console.error(e);
      showModal("Error sending OTP", 'error');
    } finally {
      setLoadingUser(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp) return showModal("Enter OTP", 'info');
    setVerifyingOtp(true);
    try {
      const res = await axios.post('/api/public/auth/verify-otp', { phone: formData.phone_num, otp });
      if (res.data.status === 'success' && res.data.verified) {
        showModal("OTP Verified!", 'success');
        setShowOtpInput(false);
        // setUserFound('found'); // Keep as is? Or just proceed to submit?

        if (res.data.token) localStorage.setItem("uid", res.data.token);

        // Proceed to Registration/Booking logic automatically or let user click Submit?
        // Let's trigger submit logic here OR set a flag that verification is done.
        // Better: call `finalSubmit()`
        finalSubmit();

      } else {
        showModal("Invalid OTP", 'error');
      }
    } catch (err) {
      console.error(err);
      showModal("Error verifying OTP", 'error');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // 1. Full Name Validation (On Type)
    if (name === 'full_name') {
      if (/[^a-zA-Z\s]/.test(value)) {
        // Invalid character detected
        setErrors(prev => ({ ...prev, full_name: "Numbers/Special characters not allowed" }));
        return; // Block input
      } else {
        // Valid input - clear error if it exists
        if (errors.full_name) {
          setErrors(prev => ({ ...prev, full_name: "" }));
        }
      }
    }

    // 2. Pincode Validation (On Type)
    if (name === 'postal_code') {
      if (selectedCountry === 'IN' || selectedCountry === 'India') {
        if (/[^0-9]/.test(value)) return; // Block non-numeric for India
        if (value.length > 6) return; // Max 6 for India
      } else {
        // Non-India: Alphanumeric only, Max 10
        if (/[^a-zA-Z0-9]/.test(value)) return; // Block special chars
        if (value.length > 10) return; // Max 10
      }
    }

    // 3. ID Number Validation (On Type) - Aadhar Numeric Only
    // 3. ID Number Validation (On Type) - Aadhar Numeric Only
    if (name === 'id_proof_number') {
      if (formData.id_proof_type === 'Aadhar Card') {
        if (/[^0-9]/.test(value)) {
          return; // Block non-numeric
        }
        if (value.length > 12) {
          return; // Block > 12 digits
        }
      } else if (['Passport', 'Driving License', 'PAN Card', 'Visa ID'].includes(formData.id_proof_type)) {
        // Alphanumeric Only for these types
        if (/[^a-zA-Z0-9]/.test(value)) return;
      }
    }

    setFormData({ ...formData, [name]: value });

    // Clear error when user types (generic)
    if (name !== 'full_name' && errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validation Functions (On Blur)
  const validateField = (name, value) => {
    let error = '';

    // Common Empty Check for required fields
    if (!value || value.toString().trim() === '') {
      if (name === 'full_name') error = "Full Name is required";
      if (name === 'email_id') error = "Email is required";
      if (name === 'address') error = "Address is required";
      if (name === 'town') error = "Town/City is required";
      if (name === 'postal_code') error = (selectedCountry === 'IN' || selectedCountry === 'India') ? "Pincode is required" : "Area Code is required";
      if (name === 'id_proof_number') error = "ID Number is required";
    } else {
      // Specific Format Validations (only if not empty)
      if (name === 'email_id') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          error = "Invalid Email Address";
        }
      }
      if (name === 'postal_code') {
        if (selectedCountry === 'IN' || selectedCountry === 'India') {
          if (!/^\d{6}$/.test(value)) {
            error = "Pincode must be exactly 6 digits";
          }
        } else {
          if (value.length > 10) error = "Max 10 characters allowed";
          else if (!/^[a-zA-Z0-9]+$/.test(value)) error = "Alphanumeric only";
        }
      }
      if (name === 'id_proof_number') {
        // Specific ID Validation
        if (formData.id_proof_type === 'Aadhar Card') {
          if (!/^\d{12}$/.test(value)) error = "Invalid Aadhar Number (Must be 12 digits)";
        } else if (formData.id_proof_type === 'PAN Card') {
          if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(value)) error = "Invalid PAN Number format";
        } else if (formData.id_proof_type === 'Driving License') {
          if (value.length < 10 || !/^[a-zA-Z0-9]+$/.test(value)) error = "Invalid Driving License";
        }
      }
    }

    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const validateGuestDetails = () => {
    const errors = [];
    const data = formData;

    // 1. Full Name
    if (!data.full_name || data.full_name.trim().length < 3) {
      errors.push("Full Name must be at least 3 characters.");
    }

    // 2. Email (Basic Regex)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!data.email_id || !emailRegex.test(data.email_id)) {
      errors.push("Please enter a valid Email Address.");
    }

    // 3. Mobile Number (10 digits)
    const phoneRegex = /^\d{10}$/;
    if (!data.phone_num || !phoneRegex.test(data.phone_num)) {
      errors.push("Mobile Number must be exactly 10 digits.");
    }

    // 4. Pincode
    if (selectedCountry === 'IN' || selectedCountry === 'India') {
      const pinRegex = /^\d{6}$/;
      if (!data.postal_code || !pinRegex.test(data.postal_code)) {
        errors.push("Pincode must be exactly 6 digits for India.");
      }
    } else {
      if (!data.postal_code || data.postal_code.trim().length === 0) {
        errors.push("Area Code / Pincode is required.");
      } else if (data.postal_code.length > 10) {
        errors.push("Area Code must be max 10 characters.");
      } else if (!/^[a-zA-Z0-9]+$/.test(data.postal_code)) {
        errors.push("Area Code must be alphanumeric.");
      }
    }

    // 5. ID Proof Number
    if (!data.id_proof_number || data.id_proof_number.trim().length === 0) {
      errors.push("ID Proof Number is required.");
    }

    // 6. Address & Town
    if (!data.address || data.address.trim().length === 0) errors.push("Address is required");
    if (!data.town || data.town.trim().length === 0) errors.push("Town/City is required");


    if (errors.length > 0) {
      alert(errors.join("\n"));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate Form Data First
    // Validate Form Data First
    const validationErrors = {};

    // Full Name
    if (!formData.full_name || formData.full_name.trim().length < 3) validationErrors.full_name = "Full Name must be at least 3 characters";

    // Email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email_id || !emailRegex.test(formData.email_id)) validationErrors.email_id = "Invalid Email Address";

    // Phone
    if (!formData.phone_num || !/^\d{10}$/.test(formData.phone_num)) validationErrors.phone_num = "Invalid Mobile Number";

    // Pincode
    if (selectedCountry === 'IN' || selectedCountry === 'India') {
      if (!formData.postal_code || !/^\d{6}$/.test(formData.postal_code)) validationErrors.postal_code = "Pincode must be 6 digits";
    } else {
      if (!formData.postal_code) validationErrors.postal_code = "Required";
      else if (formData.postal_code.length > 10) validationErrors.postal_code = "Max 10 characters";
      else if (!/^[a-zA-Z0-9]+$/.test(formData.postal_code)) validationErrors.postal_code = "Alphanumeric only";
    }

    // ID Number
    if (!formData.id_proof_number) {
      validationErrors.id_proof_number = "ID Number is required";
    } else {
      if (formData.id_proof_type === 'Aadhar Card') {
        if (!/^\d{12}$/.test(formData.id_proof_number)) validationErrors.id_proof_number = "Invalid Aadhar Number";
      } else if (formData.id_proof_type === 'PAN Card') {
        if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/i.test(formData.id_proof_number)) validationErrors.id_proof_number = "Invalid PAN Number";
      } else if (formData.id_proof_type === 'Driving License') {
        if (formData.id_proof_number.length < 10 || !/^[a-zA-Z0-9]+$/.test(formData.id_proof_number)) validationErrors.id_proof_number = "Invalid Driving License";
      }
    }

    // Address & Town
    if (!formData.address) validationErrors.address = "Address is required";
    if (!formData.town) validationErrors.town = "Town is required";

    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return; // Stop if validation fails
    }

    // Logic:
    // If new user and OTP not verified -> Send OTP, Show Input.
    // If OTP verified -> finalSubmit.

    // If existing user or flow passed
    if (!formData._id && !otpSent) {
      sendOtp();
      return;
    }
    if (!formData._id && otpSent && showOtpInput) {
      handleVerifyOtp();
      return;
    }

    finalSubmit();
  };

  const finalSubmit = async () => {
    console.log("DEBUG: Submit clicked. Data:", formData);
    // alert("Submitting details for: " + formData.full_name);

    try {
      let userResponse;
      if (userFound === 'not_found' || !formData._id) { // New User
        console.log("DEBUG: Creating New User via API...");
        // Create New User
        userResponse = await axios.post('/api/public/users', formData);
      } else {
        console.log("DEBUG: Updating User via API...", formData._id);
        // Update Existing User
        userResponse = await axios.put(`/api/public/users/${formData._id}`, formData);
      }

      console.log("DEBUG: User API Response:", userResponse.data);

      if (userResponse.data.status === 'success') {
        const finalUser = userResponse.data.data;

        // Store Token if new registration
        if (userResponse.data.token) {
          localStorage.setItem("uid", userResponse.data.token);
        }

        // Trigger Room Booking API
        try {
          // 1. Construct Payload
          const fullCheckInDate = new Date(`${dateStr} ${timeStr}`);
          const noOfRooms = selection.numRooms || 1;
          const bookingPayload = {
            user_id: finalUser._id,
            building_id: selection.building.building_id,
            room_type_id: selection.roomType.room_type_id,
            category: selection.category.category_name,
            checkin_date: fullCheckInDate,
            booking_date: fullCheckInDate,
            duration: duration,
            no_of_rooms: noOfRooms,
            no_of_adults: 1,
            no_of_children: 0,
            id_proof_type: formData.id_proof_type,
            id_proof_number: formData.id_proof_number,
            payment_amount: selection.category.price * noOfRooms * duration
          };

          const bookingResponse = await axios.post('/api/public/booking/create', bookingPayload);

          if (bookingResponse.data.status === 'success') {
            showModal('Booking Created Successfully!', 'success');
            onSuccess(bookingResponse.data.data); // Proceed to Step 5
          } else {
            alert('Booking Failed: ' + bookingResponse.data.message);
          }

        } catch (bErr) {
          console.error("Booking API Error:", bErr);
          alert('Error creating booking: ' + (bErr.response?.data?.message || bErr.message));
        }

      } else {
        alert('Failed to save guest details. ' + userResponse.data.message);
      }
    } catch (err) {
      console.error("DEBUG: Submit Error:", err);
      alert('An error occurred while saving guest details: ' + (err.response?.data?.message || err.message));
    }
  };

  return (
    <div className="login p-3 p-lg-5 shadow-sm bg-white rounded">
      <h4 className="text-center mb-4">Guest Details</h4>

      {/* Booking Summary */}
      <div className="table-responsive mb-4 bg-light p-3 rounded">
        <table className="table table-borderless mb-0">
          <tbody>
            <tr>
              <td><strong>Type:</strong> {selection.category.category_name} ({selection.roomType.room_type_name})</td>
              <td><strong>Rooms:</strong> {selection.numRooms || 1} | <strong>Dates:</strong> {formatDateStr(dateStr)} ({duration} days)</td>
              <td className="text-success fw-bold">₹ {selection.category.price * (selection.numRooms || 1) * duration}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* If came from Modal, we effectively skipped the "manual search" step. */}
      {/* We show the form directly if userFound is set (which effect does). */}
      {/* If for some reason we are here without modal, show search. */}

      {(!initialPhone && userFound !== 'found' && userFound !== 'not_found') && (
        <div className="mb-4">
          <label className="form-label fw-bold">Enter Mobile Number</label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Search by 10-digit mobile number"
              maxLength="10"
              value={phone}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*$/.test(val)) {
                  setPhone(val);
                }
              }}
              disabled={userFound === 'found' || showOtpInput}
            />
            {!showOtpInput && userFound !== 'found' && (
              <button className="btn btn-primary" type="button" onClick={handlePhoneSearch} disabled={loadingUser}>
                {loadingUser ? 'Searching...' : 'Search'}
              </button>
            )}

            {(userFound === 'found' || userFound === 'not_found') && <button className="btn btn-outline-secondary" onClick={() => { setUserFound(false); setShowOtpInput(false); setPhone(''); setOtp(''); setFormData({}); }}>Reset</button>}
          </div>

          {showOtpInput && (
            <div className="mt-3">
              {/* OTP UI */}
              <div className="input-group" style={{ maxWidth: '300px' }}>
                <input type="text" className="form-control" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} maxLength="6" placeholder="Enter the 6-digit OTP" />
                <button className="btn btn-success" onClick={handleVerifyOtp} disabled={verifyingOtp}>
                  {verifyingOtp ? 'Verifying...' : 'Verify OTP'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Registration/Details Form */}
      {(userFound === 'found' || userFound === 'not_found') && (
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Removed First/Last Name as per request (Only Full Name needed) */}
            {/* <div className="col-md-6 form-group">
                <label>First Name</label>
                <input type="text" className="form-control" name="first_name" value={formData.first_name} onChange={handleChange} required />
              </div>
              <div className="col-md-6 form-group">
                <label>Last Name</label>
                <input type="text" className="form-control" name="last_name" value={formData.last_name} onChange={handleChange} required />
              </div> */}
            <div className="col-md-6 form-group">
              <label>Full Name</label>
              <input type="text" className="form-control" name="full_name" value={formData.full_name} onChange={handleChange} onBlur={handleBlur} required />
              {errors.full_name && <small className="text-danger">{errors.full_name}</small>}
            </div>
            <div className="col-md-6 form-group">
              <label>Email</label>
              <input type="email" className="form-control" name="email_id" value={formData.email_id} onChange={handleChange} onBlur={handleBlur} required />
              {errors.email_id && <small className="text-danger">{errors.email_id}</small>}
            </div>
            <div className="col-md-12 form-group">
              <label>Address</label>
              <input type="text" className="form-control" name="address" value={formData.address} onChange={handleChange} onBlur={handleBlur} required />
              {errors.address && <small className="text-danger">{errors.address}</small>}
            </div>
            <div className="col-md-6 form-group">
              <label>Country</label>
              <select
                className="form-control form-select"
                value={selectedCountry}
                onChange={handleCountryChange}
                required
              >
                <option value="">Select Country</option>
                {Country.getAllCountries().map((country) => (
                  <option key={country.isoCode} value={country.isoCode}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6 form-group">
              <label>State</label>
              {selectedCountry && State.getStatesOfCountry(selectedCountry).length > 0 ? (
                <select
                  className="form-control form-select"
                  value={selectedState}
                  onChange={handleStateChange}
                  required
                  disabled={!selectedCountry}
                >
                  <option value="">Select State</option>
                  {State.getStatesOfCountry(selectedCountry).map((state) => (
                    <option key={state.isoCode} value={state.isoCode}>
                      {state.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter State/Region"
                  value={formData.state}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^[a-zA-Z\s]*$/.test(val)) {
                      setFormData(prev => ({ ...prev, state: val, town: '' }));
                    }
                  }}
                  required
                  disabled={!selectedCountry}
                />
              )}
            </div>

            <div className="col-md-6 form-group">
              <label>Town/City</label>
              {selectedState && City.getCitiesOfState(selectedCountry, selectedState).length > 0 ? (
                <select
                  className="form-control form-select"
                  value={formData.town}
                  onChange={(e) => setFormData(prev => ({ ...prev, town: e.target.value }))}
                  required
                  disabled={!selectedState}
                >
                  <option value="">Select City</option>
                  {City.getCitiesOfState(selectedCountry, selectedState).map((city) => (
                    <option key={city.name} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  className="form-control"
                  placeholder="Enter City Name"
                  value={formData.town}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^[a-zA-Z\s]*$/.test(val)) {
                      setFormData(prev => ({ ...prev, town: val }));
                    }
                  }}
                  required
                  disabled={!formData.state}
                />
              )}
              {errors.town && <small className="text-danger">{errors.town}</small>}
            </div>
            <div className="col-md-6 form-group">
              <label>{selectedCountry === 'IN' ? 'Pincode' : 'Area Code'}</label>
              <input type="text" className="form-control" name="postal_code" value={formData.postal_code} onChange={handleChange} onBlur={handleBlur} required />
              {errors.postal_code && <small className="text-danger">{errors.postal_code}</small>}
            </div>
            <div className="col-md-6 form-group">
              <label>ID Proof Type</label>
              <select className="form-control" name="id_proof_type" value={formData.id_proof_type} onChange={handleChange} disabled={!selectedCountry}>
                <option value="">Select ID Type</option>
                {(selectedCountry === 'IN' || selectedCountry === '') ? (
                  // Default showing Aadhar/PAN if IN or nothing selected yet (but disabled)
                  <>
                    <option>Aadhar Card</option>
                    <option>PAN Card</option>
                    <option>Driving License</option>
                  </>
                ) : (
                  <>
                    <option>Passport</option>
                    <option>Visa ID</option>
                  </>
                )}
              </select>
            </div>
            <div className="col-md-6 form-group">
              <label>ID Number</label>
              <input type="text" className="form-control" name="id_proof_number" value={formData.id_proof_number} onChange={handleChange} onBlur={handleBlur} required />
              {errors.id_proof_number && <small className="text-danger">{errors.id_proof_number}</small>}
            </div>

            <div className="col-12 mt-4 text-center">
              {showOtpInput ? (
                <div className="d-flex flex-column align-items-center gap-2">
                  <input
                    type="text"
                    className="form-control form-control-lg text-center"
                    style={{ maxWidth: '200px' }}
                    placeholder="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                  />
                  <button className="btn btn-success btn-lg px-5 rounded-pill" onClick={handleVerifyOtp} type="button">
                    {verifyingOtp ? "Verifying..." : "Verify OTP & Book"}
                  </button>
                </div>
              ) : (
                <button className="btn btn-primary btn-lg px-5 rounded-pill shadow" type="submit">
                  {!formData._id && !otpSent ? "Verify Mobile & Book" : "Confirm & Book"}
                </button>
              )}
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

const BookingRoom = () => {
  const { showModal, CustomModalComponent } = useCustomModal();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dateStr = searchParams.get('date');
  const timeStr = searchParams.get('time');
  const duration = searchParams.get('duration') || 1;


  // State for Flow
  const [step, setStep] = useState(1); // 1: Building, 2: Room Type, 3: Category, 4: Room & Form
  const [selection, setSelection] = useState({
    building: null,
    roomType: null,
    category: null,
    room: null
  });

  // Settings State (Tax/Conv config)
  const [invoiceSettings, setInvoiceSettings] = useState({
    room_tax_enabled: true,
    room_tax_rate: 5,
    room_conv_enabled: true,
    room_conv_rate: 5
  });

  // Fetch settings for invoice charges
  useEffect(() => {
    axios.get('/api/settings')
      .then(res => {
        if (res.data?.data) {
          const d = res.data.data;
          setInvoiceSettings({
            room_tax_enabled: d.room_tax_enabled !== undefined ? d.room_tax_enabled : true,
            room_tax_rate: d.room_tax_rate ?? 5,
            room_conv_enabled: d.room_conv_enabled !== undefined ? d.room_conv_enabled : true,
            room_conv_rate: d.room_conv_rate ?? 5
          });
        }
      })
      .catch(err => console.warn('Could not fetch invoice settings:', err));
  }, []);

  // Modal State
  const [paymentConfirmationModalOpen, setPaymentConfirmationModalOpen] = useState(false);
  const [initialPhone, setInitialPhone] = useState('');
  const [foundUser, setFoundUser] = useState(null);
  const [isTestingOtpEnabled, setIsTestingOtpEnabled] = useState(false);

  // Fetch settings for testing mode and other configs
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL || ''}/api/settings`)
      .then(res => {
        if (res.data && res.data.status === 'success' && res.data.data) {
          setIsTestingOtpEnabled(res.data.data.is_testing_otp_enabled === true);
        }
      })
      .catch(err => console.error("Error fetching settings for room booking:", err));
  }, []);

  // Multiple Room Selection
  const [numRooms, setNumRooms] = useState(1);
  const [numRoomsModal, setNumRoomsModal] = useState({ open: false, building: null, rt: null, cat: null });

  // Data State
  const [availabilityData, setAvailabilityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Payment UI State
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('PENDING'); // PENDING, SUCCESS, FAILED
  const [paymentMessage, setPaymentMessage] = useState('');
  const [finalPaymentDetails, setFinalPaymentDetails] = useState(null);


  // Easebuzz Payment Flow
  const handleEasebuzzPayment = async (total, booking_id, user) => {
    try {
      const orderPayload = {
        booking_id: booking_id,
        amount: total,
        customer_id: user.phone_num,
        customer_phone: user.phone_num,
        customer_name: user.full_name,
        customer_email: user.email_id
      };

      const token = localStorage.getItem('uid');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const orderRes = await axios.post('/api/payment/create-order', orderPayload, config);

      if (orderRes.data && orderRes.data.access_key) {
        // Redirect to Easebuzz
        const env = orderRes.data.env || 'prod'; // Read env from backend response
        const paymentUrl = `https://${env === 'test' ? 'testpay' : 'pay'}.easebuzz.in/pay/${orderRes.data.access_key}`;
        window.location.href = paymentUrl;
      } else {
        showModal("Failed to initiate payment with Easebuzz", 'error');
      }
    } catch (pErr) {
      console.error("Payment Init Error:", pErr);
      showModal("Error initiating payment: " + (pErr.response?.data?.message || pErr.message), 'error');
    }
  };

  useEffect(() => {
    if (dateStr) {
      fetchAvailability();
    }
  }, [dateStr, duration]);

  const fetchAvailability = async () => {
    try {
      // Fetch detailed availability
      const response = await axios.get(`/api/public/booking/availability?date=${dateStr}&time=${timeStr}&duration=${duration}`);
      if (response.data.status === 'success') {
        setAvailabilityData(response.data.data);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to fetch rooms');
    } finally {
      setLoading(false);
    }
  };

  // --- Handlers ---
  const handleSelectBuilding = (building) => {
    setSelection({ ...selection, building, roomType: null, category: null, room: null });
    setStep(2);
  };

  // Triggered when clicking a card in Step 1 (Building List "Green Area")
  const handleDirectSelect = (building, roomType, category, rooms = 1) => {
    const newSelection = {
      building: building,
      roomType: roomType,
      category: category,
      room: null,
      numRooms: rooms
    };
    setSelection(newSelection);
    // Open Modal instead of direct jump
    setPaymentConfirmationModalOpen(true);
  };

  const handleCatCardClick = (b, rt, cat) => {
    if (cat.available_count <= 0) return;
    setNumRooms(1);
    setNumRoomsModal({ open: true, building: b, rt, cat });
  };

  const handleSelectRoomType = (rt) => {
    setSelection({ ...selection, roomType: rt, category: null, room: null });
    setStep(3);
  };

  const handleSelectCategory = (cat) => {
    const newSelection = { ...selection, category: cat, room: null };
    setSelection(newSelection);
    // Open Modal
    setPaymentConfirmationModalOpen(true);
  };

  const handleSelectRoom = (room) => {
    setSelection({ ...selection, room });
  };

  const handleBack = () => {
    if (step === 4) setStep(1); // If at step 4 (Room Select), back goes to Step 1 (Building List) (or Modal cancel logic handles "back" to list naturally)
    else if (step > 1) setStep(step - 1);
  };

  const handleModalSuccess = (user, phone) => {
    setFoundUser(user);
    setInitialPhone(phone);
    setPaymentConfirmationModalOpen(false);
    setStep(4);
  };

  // --- Render Helpers ---
  // Step 1: Building List
  const renderStep1 = () => (
    <div className="d-flex flex-column gap-4">
      {availabilityData.map((b) => (
        <div
          key={b.building_id}
          className="card shadow-sm border-0 overflow-hidden"
          style={{ transition: 'transform 0.2s', minHeight: '150px' }}
        >
          <div className="d-flex flex-column flex-md-row h-100">
            {/* Image Section */}
            <div style={{ width: '100%', maxWidth: '300px', minWidth: '250px' }}>
              <img
                src={b.building_image || 'assets/img/theme-img/title_shape.svg'}
                alt={b.building_name}
                style={{ width: '100%', height: '100%', objectFit: 'cover', minHeight: '150px' }}
              />
            </div>

            {/* Content Section */}
            <div className="flex-grow-1 d-flex flex-column flex-md-row">
              {/* Building Name */}
              <div className="p-4 d-flex align-items-center justify-content-start flex-grow-1 bg-white">
                <h5 className="mb-0 text-dark fw-bold text-uppercase">{b.building_name}</h5>
              </div>

              {/* Availability & Price Grid (Green Section) */}
              <div className="p-3 text-white d-flex align-items-center justify-content-center flex-grow-1" style={{ backgroundColor: '#2e7d32' }}>
                <div className="d-flex flex-column flex-md-row w-100 h-100 gap-3">
                  {b.room_types.map((rt, index) => (
                    <div key={rt.room_type_id} className="flex-fill px-3 d-flex flex-column justify-content-center">
                      <strong className="d-block text-uppercase text-center text-warning mb-3" style={{ fontSize: '1.2rem', letterSpacing: '1px' }}>
                        {rt.room_type_name}
                      </strong>
                      <div className="d-flex flex-column gap-3">
                        {rt.categories.map(cat => (
                          <div
                            key={cat.category_name}
                            className="d-flex flex-column w-100 bg-white bg-opacity-10 p-3 rounded"
                            style={{ cursor: cat.available_count > 0 ? 'pointer' : 'not-allowed', transition: 'background-color 0.2s', opacity: cat.available_count > 0 ? 1 : 0.6 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCatCardClick(b, rt, cat);
                            }}
                            onMouseOver={(e) => { if (cat.available_count > 0) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)' }}
                            onMouseOut={(e) => { if (cat.available_count > 0) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)' }}
                          >
                            <div className="d-flex flex-column align-items-start gap-1">
                              <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#0f0e0eff' }}>
                                {cat.category_name}
                              </span>
                              <span className="fw-bold bg-white text-success px-2 py-0 rounded" style={{ fontSize: '1rem' }}>
                                Rs. {cat.price}/-
                              </span>
                              <span className="text-light mt-1" style={{ fontSize: '0.9rem' }}>
                                Rooms Available : <span className="text-warning fw-bold" style={{ fontSize: '1.2rem' }}>{cat.available_count}</span>
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {b.room_types.length === 0 && <div className="text-center opacity-50 w-100">No Data</div>}
              </div>
            </div>
          </div>
        </div>
      ))}
      {availabilityData.length === 0 && !loading && <div className="text-center p-5 text-muted">No buildings found for the selected criteria.</div>}
    </div>
  );

  // Step 2: Room Type (Single/Double) for selected Building
  const renderStep2 = () => {
    const b = selection.building;
    if (!b) return null;
    return (
      <div className="text-center">
        <h4 className="mb-4">Select Room Type in {b.building_name}</h4>
        <div className="d-flex justify-content-center gap-3">
          {b.room_types.map(rt => (
            <div key={rt.room_type_id}
              className="card p-4 shadow-sm"
              style={{ width: '200px', cursor: 'pointer', backgroundColor: '#f8f9fa' }}
              onClick={() => handleSelectRoomType(rt)}>
              <h5>{rt.room_type_name}</h5>
              <p className="small text-muted mb-0">Select</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Step 3: Category (AC/Non-AC) for selected Room Type
  const renderStep3 = () => {
    const rt = selection.roomType;
    if (!rt) return null;
    return (
      <div className="text-center">
        <h4 className="mb-4">Select Category for {rt.room_type_name}</h4>
        <div className="d-flex justify-content-center gap-3">
          {rt.categories.map(cat => (
            <div key={cat.category_name}
              className="card p-4 shadow-sm"
              style={{ width: '250px', cursor: 'pointer', border: '1px solid #ddd' }}
              onClick={() => handleSelectCategory(cat)}>
              <h5 className="mb-2">{cat.category_name}</h5>
              <h6 className="text-success mb-2">Available: {cat.available_count}</h6>
              <h6 className="text-primary">₹ {cat.price}</h6>
            </div>
          ))}
        </div>
      </div>
    );
  };


  const [bookingDetails, setBookingDetails] = useState(null);

  const handleBookingCreated = (details) => {
    setBookingDetails(details);
    setStep(5);
  };

  // Step 4: Guest Details Form (Room Selection Removed)
  const renderStep4 = () => {
    const cat = selection.category;
    if (!cat) return null;

    return (
      <div>
        <h4 className="text-center mb-4">Guest Details for {selection.roomType.room_type_name} - {cat.category_name}</h4>
        {/* Guest Details Form */}
        <GuestDetailsForm
          selection={selection}
          dateStr={dateStr}
          timeStr={timeStr}
          duration={duration}
          onSuccess={handleBookingCreated}
          initialUser={foundUser}
          initialPhone={initialPhone}
          showModal={showModal}
          isTestingOtpEnabled={isTestingOtpEnabled}
        />
      </div>
    );
  };

  // Step 5: Booking Confirmation / Invoice
  const renderStep5 = () => {
    if (!bookingDetails) return <div className="text-center">Loading Invoice...</div>;

    const { user_id: user, booking_id, created_date, checkin_date, check_out_date } = bookingDetails;

    const baseRent = selection.category.price * (selection.numRooms || 1) * duration;
    const tax = invoiceSettings.room_tax_enabled ? Math.round(baseRent * (invoiceSettings.room_tax_rate / 100)) : 0;
    const conv = invoiceSettings.room_conv_enabled ? Math.round(baseRent * (invoiceSettings.room_conv_rate / 100)) : 0;
    const total = baseRent + tax + conv;

    const downloadReceipt = async (id) => {
      try {
        const response = await fetch('/api/payment/receipt/room', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id })
        });

        if (!response.ok) throw new Error("Failed to download receipt");

        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `receipt_room_${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } catch (err) {
        console.error(err);
        showModal("Error downloading receipt: " + err.message, 'error');
      }
    };

    return (
      <div className="container mt-4 mb-5">
        <div className="card shadow-sm border" style={{ maxWidth: '900px', margin: '0 auto' }}>

          {/* Header */}
          <div className="card-header bg-white text-center py-3">
            <h4 className="m-0 fw-bold" style={{ color: '#a71919' }}>{user.phone_num} - Room Invoice</h4>
          </div>

          <div className="card-body p-4">

            {/* Top Row: Date & Room Type */}
            <div className="d-flex justify-content-between align-items-start mb-4">
              <div>
                <div className="fw-bold small text-muted">Booking Date:</div>
                <div className="fw-bold">{formatDateObj(created_date)}</div>
              </div>
              <div>
                <h4 className="fw-bold text-danger text-uppercase">{selection.category.category_name}</h4>
              </div>
            </div>

            {/* Receipt ID */}
            <div className="mb-3">
              <span className="fw-bold small text-muted">Receipt ID: </span>
              <span className="fw-bold">{booking_id}</span>
            </div>

            {/* Guest Details */}
            <div className="mb-3">
              <div className="fw-bold text-muted small">Name: <span className="text-dark fs-6">{user.full_name},</span></div>
              <div className="fw-bold text-muted small">Mobile: <span className="text-dark">{user.phone_num}</span>, Email: <span className="text-dark">{user.email_id}</span></div>
              <div className="fw-bold text-muted small">Address: <span className="text-dark">{user.address}, {user.town}, {user.state} {user.postal_code}</span></div>
            </div>

            {/* Room Details */}
            <div className="mb-4">
              <div className="fw-bold text-muted small">Building: <span className="text-dark">{selection.building.building_name}</span></div>
              <div className="fw-bold text-muted small">Room Type: <span className="text-dark">{selection.roomType.room_type_name}</span></div>
              <div className="fw-bold text-muted small">Duration: <span className="text-dark">{duration} Day(s)</span></div>
            </div>

            {/* Note */}
            <div className="alert alert-info border-0 rounded-0 mb-4" style={{ backgroundColor: '#e0f7fa', color: '#006064' }}>
              <strong>Note:</strong> Room number will be allocated at check-in.
            </div>

            <hr className="my-4" />

            {/* Dates & Pricing Row */}
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <div className="fw-bold small text-muted">Check-In Date:</div>
                  <div className="text-dark fw-bold">{formatDateObj(checkin_date)}</div>
                </div>
                <div>
                  <div className="fw-bold small text-muted">Check-Out Date:</div>
                  <div className="text-dark fw-bold">{formatDateObj(check_out_date)}</div>
                </div>
              </div>

              <div className="col-md-6 text-md-end">
                <div className="mb-2">
                  <span className="text-muted">Rs. {baseRent}/-</span>
                </div>
                {invoiceSettings.room_tax_enabled && (
                  <div className="mb-1 text-muted small">Tax {invoiceSettings.room_tax_rate}% Rs. {tax}/-</div>
                )}
                {invoiceSettings.room_conv_enabled && (
                  <div className="mb-3 text-muted small">Conv. {invoiceSettings.room_conv_rate}% Rs. {conv}/-</div>
                )}
              </div>
            </div>

            <hr className="my-4" />

            {/* Total */}
            <div className="text-end">
              <h3 className="fw-bold text-danger">Total Rs. {total}/-</h3>
            </div>

          </div>

          {/* Footer Buttons */}
          <div className="card-footer bg-white d-flex justify-content-between p-4 border-top-0">
            <button className="btn btn-dark" onClick={() => {
              if (window.confirm('Are you sure you want to cancel?')) {
                navigate('/');
              }
            }}>Cancel</button>

            <button className="btn btn-dark px-5" onClick={() => handleEasebuzzPayment(total, booking_id, user)}>PAY</button>
          </div>
        </div>

        <PaymentConfirmationModal
          isOpen={paymentModalOpen}
          status={paymentStatus}
          paymentDetails={finalPaymentDetails}
          message={paymentMessage}
          onRetry={() => setPaymentModalOpen(false)}
          onClose={() => {
            setPaymentModalOpen(false);
            navigate('/profile');
          }}
          onDownloadReceipt={() => downloadReceipt(booking_id)}
        />
      </div >
    );
  };



  if (loading) return <div className="text-center p-5">Loading availability...</div>;
  if (error) return <div className="text-center p-5 text-danger">{error}</div>;

  return (
    <>
      <CustomModalComponent />
      <div className="my-account space-extra-bottom pt-5" style={{ background: '#f9f9f9', minHeight: '100vh' }}>
        <div className="container">
          <div className="title-area text-center mb-4">
            <h2 className="sec-title">Room Booking</h2>
            <p className="text-muted">
              {step === 5 && bookingDetails
                ? <>Check-In: <strong>{formatDateObj(bookingDetails.checkin_date)}</strong> &nbsp;|&nbsp; Check-Out: <strong>{formatDateObj(bookingDetails.check_out_date)}</strong></>
                : <>{formatDateStr(dateStr)} | Time: {timeStr} | Duration: {duration} Day(s)</>}
            </p>
          </div>

          {step > 1 && (
            <button className="btn btn-link text-muted mb-3" onClick={handleBack}>
              &larr; Back to previous step
            </button>
          )}



          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
          {step === 5 && renderStep5()}

          {/* Room Quantity Picker Modal */}
          {numRoomsModal.open && (
            <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
              <div style={{ background: '#fff', borderRadius: 14, padding: '32px 28px', minWidth: 320, maxWidth: 420, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' }}>
                <h5 className="fw-bold mb-1" style={{ color: '#1a1a1a' }}>Select Number of Rooms</h5>
                <p className="text-muted small mb-1">{numRoomsModal.cat?.category_name} — <strong style={{ color: '#2e7d32' }}>Rs. {numRoomsModal.cat?.price}/-</strong> per room/night</p>
                <p className="text-muted small mb-4">Available: <span className="fw-bold text-warning">{numRoomsModal.cat?.available_count}</span> room(s)</p>

                <div className="d-flex align-items-center justify-content-center gap-4 mb-4">
                  <button
                    className="btn btn-outline-secondary rounded-circle"
                    style={{ width: 44, height: 44, fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setNumRooms(prev => Math.max(1, prev - 1))}
                  >−</button>
                  <span style={{ fontSize: 36, fontWeight: 'bold', minWidth: 50, textAlign: 'center', color: '#1a1a1a' }}>{numRooms}</span>
                  <button
                    className="btn btn-outline-secondary rounded-circle"
                    style={{ width: 44, height: 44, fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={() => setNumRooms(prev => Math.min(numRoomsModal.cat?.available_count || 1, prev + 1))}
                  >+</button>
                </div>

                <div className="text-center mb-4 p-3 rounded" style={{ background: '#f1f8e9', border: '1px solid #c5e1a5' }}>
                  <div className="small text-muted">Estimated Total</div>
                  <div className="fw-bold" style={{ fontSize: '1.3rem', color: '#2e7d32' }}>
                    Rs. {(numRoomsModal.cat?.price || 0) * numRooms} × {duration} day(s) = <span style={{ color: '#a71919' }}>Rs. {(numRoomsModal.cat?.price || 0) * numRooms * duration}/-</span>
                  </div>
                </div>

                <div className="d-flex gap-3">
                  <button
                    className="btn btn-outline-secondary flex-fill"
                    onClick={() => setNumRoomsModal({ open: false, building: null, rt: null, cat: null })}
                  >Cancel</button>
                  <button
                    className="btn btn-dark flex-fill fw-bold"
                    onClick={() => {
                      const { building, rt, cat } = numRoomsModal;
                      setNumRoomsModal({ open: false, building: null, rt: null, cat: null });
                      handleDirectSelect(building, rt, cat, numRooms);
                    }}
                  >Next →</button>
                </div>
              </div>
            </div>
          )}

          {/* Booking Phone/OTP Modal */}
          {paymentConfirmationModalOpen && (
            <BookingModal
              show={paymentConfirmationModalOpen}
              onClose={() => setPaymentConfirmationModalOpen(false)}
              selection={selection}
              onSuccess={handleModalSuccess}
              dateStr={dateStr}
              timeStr={timeStr}
              duration={duration}
              showModal={showModal}
              isTestingOtpEnabled={isTestingOtpEnabled}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default BookingRoom;
